import { Image, Platform, TextInput } from 'react-native';
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';

import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import ScreenWrapper from '@hoc/ScreenWrapper';
import InputText from '@sharedComponents/TextInput/TextInputV2';
import Button from '@sharedComponents/Button/Button';
import CheckBox from '@sharedComponents/CheckBox/CheckBox';
import KeyboardDismissView from '@hoc/KeyboardDismissView';
import { PublicStackParamList } from 'src/navigationApp/types';
import { endpoints } from '@api/apis';
import ROUTE from '../../navigationApp/routes'
import { Theme, ThemedPressable, ThemedText, ThemedView } from '../../theme'
import { getPrivacyLinks, getTermsLinks, openURL, trackEvent } from '../../utils'
import { useAppSelector } from '../../store/hooks'
import { CheckCircle } from '@sharedComponents/Icons'
import {useDarkMode} from '@app/utils';
import { removeUrlWithoutReload } from "@app/utils"
import AsyncStorage from '@react-native-async-storage/async-storage';
import { PLATFORM_NAME } from '@app/types/regix';
import { useTheme } from '@shopify/restyle';


type ScreenProps = NativeStackScreenProps<
  PublicStackParamList,
  'LOGIN_MOBILE_NUMBER_SCREEN'
>;

const LoginScreen = ({ navigation, route }: ScreenProps) => {
  const  user  = useAppSelector(state => state?.platformParams?.user)
  const  leads  = useAppSelector(state => state?.platformParams?.leads)
  const { isErrorSheetVisible, errorMessage } = useAppSelector(state => state.errorSheet)
  const [mobileNumber, setMobileNumber] = useState<string>('');
  const [isMobileNumberValid, setIsMobileNumberValid] =
    useState<boolean>(false);
  const [networkError,setNetWorkError] = useState<boolean>(false);
  const [isTncChecked, setIsTncChecked] = useState<boolean>(true);
  const [hasError, setHasError] = useState<string>('');
  const [buttonLoader, setButtonLoader] = useState<boolean>(false);
  const isDarkMode = useDarkMode()
  const inputRef = useRef<TextInput>(null);
  const [isLimitReached, setIsLimitReached] = useState<boolean>(false)
  const {colors} = useTheme<Theme>()

  const { t } = useTranslation('translation', {
    keyPrefix: route?.name as String,
  });

  const handleCheckBoxToggle = (): void => {
    if(!isTncChecked){
      trackEvent('LOGIN_SCREEN_MOBLOGIN_T&C_UNCHECKED')
    } else {
      trackEvent('LOGIN_SCREEN_MOBLOGIN_T&C_CHECKED')
    }
    setIsTncChecked(!isTncChecked);
  };
  const isFocused = useIsFocused();

  const platform = useAppSelector(state => state?.platformParams.platform);
  const fromChoice = platform === PLATFORM_NAME.CHOICE && Platform.OS === 'web';



  


  useEffect(() => {
    // temp to fix edge case of access token getting sent with public apis 
    // this is a failsafe which clears the token if in any case it was found 

    if(Platform.OS === 'web') {
      removeUrlWithoutReload("startnew")
    }

    AsyncStorage.removeItem('access_token');
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      },Platform.select({
        ios : 200,
        web : 200,
        android : 500
      }))
    } 
    if (user || leads) {
      onInput(String(user??leads??''))
    }
  }, []);

  useEffect(()=>{
    if(isErrorSheetVisible && errorMessage==='Network Error'){
      setNetWorkError(true)
    }
  },[isErrorSheetVisible,errorMessage])


  useFocusEffect(
    React.useCallback(() => {
      if (isFocused && Platform.OS !== 'web') {
        // Reset error and focus input when user comes back to the screen.
        setHasError('');
        setTimeout(() => {
          inputRef.current?.focus();
        }, Platform.select({
          ios : 0,
          web : 200,
          android : 500
        }));
      }
    }, [isFocused])
  );

  const triggerOtp = (): void => {
    trackEvent('LOGIN_SCREEN_MOBLOGIN_CONTINUE_CLICKED',{source_page: 'LOGIN_MOBILE_NUMBER_SCREEN'})
    inputRef.current?.blur()
    setButtonLoader(true);
    const url = `${endpoints.generateLoginOTP}/+91${mobileNumber}?enableWhatsapp=true`;
    axios
      .get(url)
      .then(() => {
        navigation.navigate(ROUTE.LOGIN_OTP_SCREEN, {
          phoneNumber: mobileNumber,
        });
      })
      .catch(err => {
        console.error('err', err);
        if (err?.status === 400 || err?.response?.status === 400) {
          setHasError(err?.data?.message);
          if(err?.data?.voltErrorCode === 'ACCOUNT_BLOCKED_FROM_OTP') {
            setIsLimitReached(true)
          }
        }
        else {
          // generic error handling 
          setHasError('Something went wrong! Please try again')
        }
      })
      .finally(() => {
        setButtonLoader(false);
      });
  };

  const onInput = (text: string) => {
    setHasError('')
    setIsLimitReached(false)
    let formattedText = text.replace(/[^0-9]/g, '');
    setNetWorkError(false)
    if (formattedText.length > 10) {
      formattedText = formattedText.slice(0, 10);
    }
    setMobileNumber(formattedText);
    if (formattedText.length === 10) {
      trackEvent('LOGIN_SCREEN_MOBLOGIN_MOBNO_FOCUSED',{phoneNumber: formattedText})
      setIsMobileNumberValid(true);
    } else {
      setIsMobileNumberValid(false);
    }
  };

  const onBlur = () => {
    if(inputRef.current){
      inputRef.current?.blur()
    }
    if (mobileNumber.length < 10 && mobileNumber.length > 0) {
      setHasError('Enter a 10 digit mobile number');
    } else {
      setHasError('');
    }
  }

  //Use OLD Typography until we move to new design

  return (
    <ScreenWrapper screenName={route?.name} additionalProperties={{newArchRoute : true}}>
      <KeyboardDismissView>
        <ThemedView backgroundColor="bg-surface-primary" flex={1} padding="lg">
          <ThemedView mt="2xl" />
          {fromChoice ? (
            <ThemedText color='text-primary' variant='h3b'>Continue your loan application</ThemedText>
          ) : (
            <ThemedText color='text-primary' variant='h3b'>{t('heading')}</ThemedText>
          )}
          <ThemedView mt="xs" mb="4xl">
           <ThemedText color='text-secondary' variant='b3r'>{t('subHeading')}</ThemedText>
          </ThemedView>
          <InputText
            ref={inputRef}
            keyboardType="phone-pad"
            onChangeText={onInput}
            onBlur={onBlur}
            value={mobileNumber}
            showAnimation
            showLabel
            label={t('inputLabel')}
            showLeftAccessory
            isDarkMode={Boolean(isDarkMode)}
            leftAccessory={() => {
              return (
                <ThemedView style={{ marginRight: 5 }}>
                  <ThemedText lineHeight={20} color='text-placeholder' variant='b2r'>{t('indPhonePrefix')}</ThemedText>
                </ThemedView>
              );
            }}
            showRightAccessory={mobileNumber.length >= 1}
            rightAccessory={() => {
              return (
                <ThemedView flexDirection="row" alignItems="center">
                  {isMobileNumberValid ? (
                    <CheckCircle
                      size={20}
                      weight="fill"
                      fill={colors['icon-success']}
                    />
                  ) : (
                    <ThemedText color='text-secondary' variant='b4r'>{`${mobileNumber?.length}/10`}</ThemedText>
                  )}
                </ThemedView>
              );
            }}
            showErrorMessage={true}
            hasError={hasError}
            maxLength={10}
          />
          <Button
            size='MD'
            label={'Continue'}
            handlePress={triggerOtp}
            type={'SOLID'}
            disabled={!isTncChecked || !isMobileNumberValid || networkError || isLimitReached}
            buttonLoader={buttonLoader}
            eventName='CONTINUE_BUTTON'
          />
          <ThemedView flexDirection="row" mt="2xl" alignItems="center">
            <CheckBox
              size={16}
              onToggle={handleCheckBoxToggle}
              isChecked={isTncChecked}
            />
            <ThemedView flexDirection="row" alignItems="center">
              <ThemedText color='text-secondary' variant='b5r'>{t('tnc1') + ' '}</ThemedText>
              <ThemedPressable eventName='TNC_LINK'
                onPress={() =>
                  openURL(
                    getTermsLinks()
                  )
                }
              >
              <ThemedText nativeID='txt_loginScreen_termsCondition' color='text-action-primary' variant='b5r'>{' ' + t('tnc2') + ' '}</ThemedText>
              </ThemedPressable>
              <ThemedText color='text-secondary' variant='b5r'>{t('tnc3') + ' '}</ThemedText>
              <ThemedPressable
                eventName='PRIVACY_POLICY_LINK'
                onPress={() =>
                  openURL(
                    getPrivacyLinks()
                  )
                }>
                <ThemedText nativeID="txt_loginScreen_privacyPolicy" color='text-action-primary' variant='b5r'>{t('tnc4') + '.'}</ThemedText>
              </ThemedPressable>
            </ThemedView>
          </ThemedView>
         
        </ThemedView>
        
      </KeyboardDismissView>
       {
        (fromChoice) && (
          <ThemedView  backgroundColor='bg-surface-primary' borderTopWidth={1} paddingVertical='lg' borderColor='border-primary' justifyContent="center" alignItems="center">
            <ThemedView flexDirection='row' bg='bg-fill-emphasis-primary-1' padding='md' borderRadius='sm'>
              <Image style={{ height: 24, width: 64 }} source={{ uri: 'https://volt-images.s3.ap-south-1.amazonaws.com/volt-website-header.svg' }} />
              <ThemedText marginLeft='sm' marginRight='sm'>X</ThemedText>
              <Image style={{ height: 24, width: 70 }} source={{ uri: 'https://volt-images-alpha.s3.ap-south-1.amazonaws.com/choice_logo.png' }} />
            </ThemedView>

          </ThemedView>
        )
        }
    </ScreenWrapper>
);
};

export default LoginScreen;
